import { useEffect, useRef } from 'react';
import { FieldErrors, FieldValues, UseControllerProps } from 'react-hook-form';

import { useServiceFeatures } from 'hooks/useServiceContext';
import { useToast } from 'hooks/useToaster';
import { getCustomers } from 'lib/api/getCustomers';
import { SyncApproach } from 'types/Integration';
import { NormalizedLoad, TMSCustomer } from 'types/Load';
import { Maybe } from 'types/UtilityTypes';
import { TMS, integrationNameMap } from 'types/enums/Integrations';
import {
  GenericCompanySearchableFields,
  customerSearchHandler,
  injectSelectedObject,
  mapCustomerToSelectOptions,
  normalizeExternalTMSID,
  resolveSelectedItem,
  shouldUpgradeTMSRecord,
} from 'utils/loadInfoAndBuilding';

import { RHFDebounceSelect } from './RHFDebounceSelect';

type RHFCustomerSelectProps<TFieldValues extends FieldValues = NormalizedLoad> =
  Pick<
    UseControllerProps<TFieldValues>,
    'name' | 'rules' | 'control' | 'disabled'
  > & {
    label?: string; // Default = 'Name'
    errors: FieldErrors;
    customers: Maybe<TMSCustomer[]>;
    setCustomers: React.Dispatch<React.SetStateAction<Maybe<TMSCustomer[]>>>;
    isLoadingCustomers: boolean;
    setIsLoadingCustomers: React.Dispatch<React.SetStateAction<boolean>>;
    required?: boolean; // Default = true
    placeholder?: string; // Default = 'Search by name or address'
    tmsID?: number; // Optional TMS ID, will use from context if not provided
    aiHighlightedExternalTMSID?: string; // If provided, the matching option will be highlighted with AI sparkle icon
    // Opt into preserving a server-search selection when the initial options are restored.
    preserveSearchedSelection?: boolean;
    filterCustomers?: (customers: TMSCustomer[]) => TMSCustomer[];
    // Default search field sent to the API.
    // Use 'name' for name-only queries (e.g. load building forms); 'nameAddress' is the default.
    // See GenericCompanySearchableFields in loadInfoAndBuilding.ts for more options.
    searchFieldDefault?: GenericCompanySearchableFields;
  };

export function RHFCustomerSelect<
  TFieldValues extends FieldValues = NormalizedLoad,
>({
  name,
  label = 'Name',
  disabled,
  control,
  errors,
  rules,
  customers,
  setCustomers,
  isLoadingCustomers,
  setIsLoadingCustomers,
  required = true,
  placeholder = 'Search by name or address',
  tmsID,
  aiHighlightedExternalTMSID,
  preserveSearchedSelection = false,
  filterCustomers,
  searchFieldDefault = 'nameAddress',
}: RHFCustomerSelectProps<TFieldValues>) {
  const { toast } = useToast();
  const { tmsIntegrations } = useServiceFeatures();
  const effectiveTmsID = tmsID ?? tmsIntegrations?.[0]?.id;

  const originalCustomersRef = useRef<Maybe<TMSCustomer[]>>(null);
  const selectedSearchCustomerRef = useRef<Maybe<TMSCustomer>>(null);

  useEffect(() => {
    if (customers) {
      if (
        !originalCustomersRef.current ||
        customers.length >= (originalCustomersRef.current.length || 0) ||
        !originalCustomersRef.current.length
      ) {
        originalCustomersRef.current = customers;
      }
    }
  }, [customers]);

  const handleCustomerSearch = async (
    field: GenericCompanySearchableFields,
    value: string
  ) => {
    if (!effectiveTmsID) {
      return [];
    }

    const customersToSearch = originalCustomersRef.current || customers;

    return customerSearchHandler({
      tmsID: effectiveTmsID,
      customers: customersToSearch,
      setCustomers,
      field,
      value,
      filterCustomers,
    });
  };

  const handleRefreshCustomers = async () => {
    if (!effectiveTmsID) {
      toast({
        description: 'No TMS integration available.',
        variant: 'destructive',
      });
      return;
    }

    try {
      setIsLoadingCustomers(true);
      const response = await getCustomers(effectiveTmsID, true);

      if (response.isOk()) {
        const freshCustomers = response.value.customerList;
        setCustomers(freshCustomers);
        originalCustomersRef.current = freshCustomers;
        toast({
          description: 'Successfully refreshed customer list.',
          variant: 'success',
        });
        return;
      }

      toast({
        description:
          response.error.message || 'Failed to refresh customer list.',
        variant: 'destructive',
      });
    } catch {
      toast({
        title: 'Error',
        description: 'Failed to refresh customers',
        variant: 'destructive',
      });
    } finally {
      setIsLoadingCustomers(false);
    }
  };

  // Capture the full customer object when selected (via parentOnChange, before reset happens)
  const handleCustomerSelection = (
    value: unknown,
    selectedRawItem?: unknown
  ) => {
    if (!value) {
      return;
    }

    const valueToUse =
      typeof value === 'object' && value !== null && 'value' in value
        ? (value as { value: string }).value
        : value;
    const id = normalizeExternalTMSID(valueToUse);
    if (!id) {
      return;
    }

    const selectedCustomer = resolveSelectedItem<TMSCustomer>(
      id,
      customers,
      selectedRawItem,
      value
    );
    if (!selectedCustomer?.externalTMSID) {
      return;
    }

    const baseline = originalCustomersRef.current;
    const isFromSearch =
      baseline != null &&
      !baseline.some(
        (customer) => normalizeExternalTMSID(customer.externalTMSID) === id
      );
    const baselineRow = baseline?.find(
      (customer) => normalizeExternalTMSID(customer.externalTMSID) === id
    );
    const isSparseUpgrade =
      baselineRow != null &&
      shouldUpgradeTMSRecord(selectedCustomer, baselineRow);

    if (
      (isFromSearch || isSparseUpgrade) &&
      selectedCustomer.externalTMSID &&
      (preserveSearchedSelection || selectedCustomer.ID || isSparseUpgrade)
    ) {
      selectedSearchCustomerRef.current = selectedCustomer;
    } else if (!isFromSearch && !isSparseUpgrade) {
      selectedSearchCustomerRef.current = null;
    }

    setCustomers((prev) => injectSelectedObject(selectedCustomer, prev ?? []));
    if (isSparseUpgrade && originalCustomersRef.current) {
      originalCustomersRef.current = injectSelectedObject(
        selectedCustomer,
        originalCustomersRef.current
      );
    }
  };

  const handleResetOptions = () => {
    if (originalCustomersRef.current) {
      // Preserve the stored search customer when resetting.
      if (selectedSearchCustomerRef.current) {
        setCustomers(
          injectSelectedObject(
            selectedSearchCustomerRef.current,
            originalCustomersRef.current ?? []
          )
        );
        return;
      }

      setCustomers(originalCustomersRef.current);
    }
  };

  // Determine which TMS is in use based on the integration that will be used for API calls
  const selectedIntegration = effectiveTmsID
    ? tmsIntegrations?.find((t) => t.id === effectiveTmsID)
    : tmsIntegrations?.[0];

  const isGlobalTranzTMS = selectedIntegration?.name === TMS.GlobalTranzTMS;
  const shouldPreserveSearchedSelection =
    isGlobalTranzTMS || preserveSearchedSelection;

  const isPLS = selectedIntegration?.name === TMS.PLS;
  const isRefreshDisabled =
    !isPLS &&
    (selectedIntegration?.customerSyncApproach === SyncApproach.FullSync ||
      selectedIntegration?.customerSyncApproach === SyncApproach.NotSupported ||
      selectedIntegration?.customerSyncApproach ===
        SyncApproach.SequentialFullSync);

  const formattedTMSName = selectedIntegration?.name
    ? // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore name is TMS enum or other matching type
      integrationNameMap[selectedIntegration.name] || selectedIntegration.name
    : 'your TMS';

  const refreshTooltip = isRefreshDisabled
    ? `Not seeing a recently added customer? Due to ${formattedTMSName} constraints, ${formattedTMSName} gets new customers daily.`
    : undefined;

  return (
    <RHFDebounceSelect
      required={required}
      name={name}
      label={label}
      disabled={disabled}
      control={control}
      errors={errors}
      rules={rules}
      data={customers}
      isLoading={isLoadingCustomers}
      refreshHandler={handleRefreshCustomers}
      refreshDisabled={isRefreshDisabled}
      refreshTooltip={refreshTooltip}
      resetOptionsHandler={handleResetOptions}
      fetchOptions={handleCustomerSearch}
      mapOptions={mapCustomerToSelectOptions}
      showSearchParamDropdown={false}
      searchFieldDefault={searchFieldDefault}
      placeholder={placeholder}
      parentOnChange={
        shouldPreserveSearchedSelection ? handleCustomerSelection : undefined
      }
      callFieldOnChangeWithParent={shouldPreserveSearchedSelection}
      aiHighlightedExternalTMSID={aiHighlightedExternalTMSID}
    />
  );
}
